import React, { useState, useCallback, useMemo, useRef } from 'react'
import { Autocomplete, Box, Checkbox, Paper, TextField, Typography, FormLabel, Chip } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CancelIcon from '@mui/icons-material/Cancel'
import { grey } from '../../../colors'
import { type SxProps, type Theme, useTheme } from '@mui/material/styles'
import { Controller, useFormContext, useFormState, useWatch } from 'react-hook-form'
import api from '../../../utils/api'
import { debounce } from 'lodash'
import { ReactComponent as SelectedCheckBox } from '../../../assets/check-box-selected.svg'
import { useQuery } from '@tanstack/react-query'
import { transformAttributesListForDropdown } from '../../../helpers/transformAttributesListForDropdown'
import { type IAttribute } from '../../../models/TermsSection'
import { ATTRIBUTE_TYPE_CHIP } from '../../../consts'
import SearchRoundedIcon from '@mui/icons-material/SearchRounded'
const icon = <Box border={`1px solid ${grey[350]}`} borderRadius="4px" width="18px" height="18px" />

interface AttributesMultiSelectProps {
  name: string
  disabled?: boolean
  skip?: boolean
  extraParams?: Record<string, any>
  required?: boolean
  defaultAttributes?: IAttribute[]
  getOptionDisabled?: (option: any) => boolean
  placeholder?: string
  textFieldStyles?: SxProps<Theme>
  // When true, the option row renders a right-aligned colored chip whose label is
  // the attribute type. Default off so existing consumers stay unchanged.
  showAttributeTypeChip?: boolean
  // When provided, the dropdown only lists options whose attributeType is in the set.
  // Default undefined so existing consumers stay unchanged.
  allowedTypes?: string[]
}

const AttributesMultiSelect: React.FC<AttributesMultiSelectProps> = ({
  name,
  disabled = false,
  skip = false,
  extraParams = {},
  required = false,
  defaultAttributes,
  getOptionDisabled,
  placeholder = 'Type to search…',
  textFieldStyles,
  showAttributeTypeChip = false,
  allowedTypes
}) => {
  const theme = useTheme()
  const [input, setInput] = useState<string>('')
  // Debounced input handler
  const onInputChange = useCallback((value: string) => {
    setInput(value)
  }, [])

  const debouncedInputChange = useCallback(debounce(onInputChange, 1000), [onInputChange])
  // Use extraParams and input for query key instead of name to enable caching across multiple instances
  // This prevents duplicate API calls when multiple AttributesMultiSelect components use the same attributeType
  const { data: attributesList, isLoading, isFetching } = useQuery<any[]>({
    queryKey: ['attributesList', extraParams, input],
    enabled: !skip,
    queryFn: async (): Promise<any[]> => {
      const result = await api.get('/api/attributes/', {
        params: { pageSize: 10, search: input, ...extraParams },
        // indexes: null serializes array params as repeated bare names
        // (?attribute_type=A&attribute_type=B) which is what the BE's
        // request.args.getlist expects, instead of the axios default
        // bracketed form (?attribute_type[]=A&attribute_type[]=B).
        paramsSerializer: { indexes: null }
      })
      return result.data
    },
    staleTime: 5 * 60 * 1000
  })
  const { control, setValue } = useFormContext()
  const { errors } = useFormState({ control, name })
  const errorMessage = errors?.[name]?.message ?? null
  const currentValue = useWatch({ control, name })
  const hydratedDefaultAttributesSignatureRef = useRef<string | null>(null)

  const transformedAttributes = transformAttributesListForDropdown(attributesList ?? [])
  const displayedOptions = useMemo(() => {
    if (!allowedTypes || allowedTypes.length === 0) return transformedAttributes ?? []
    return (transformedAttributes ?? []).filter((opt: any) =>
      typeof opt?.attributeType === 'string' && allowedTypes.includes(opt.attributeType)
    )
  }, [transformedAttributes, allowedTypes])
  const transformedDefaultAttributes = useMemo(() => (
    defaultAttributes?.map((attr: any) => ({
      ...attr,
      title: attr.title || attr.attributeKey || attr.key,
      value: attr.value || attr.attributeValue || attr.values?.[0]
    })) ?? []
  ), [defaultAttributes])

  const defaultAttributesSignature = useMemo(() => (
    transformedDefaultAttributes
      .map((attr: IAttribute) => `${attr.title}::${attr.value}::${attr.isInherited ? '1' : '0'}`)
      .join('|')
  ), [transformedDefaultAttributes])

  React.useEffect(() => {
    if (defaultAttributesSignature.length === 0) {
      hydratedDefaultAttributesSignatureRef.current = null
      return
    }

    if (hydratedDefaultAttributesSignatureRef.current === defaultAttributesSignature) {
      return
    }

    hydratedDefaultAttributesSignatureRef.current = defaultAttributesSignature

    if (!currentValue || currentValue.length === 0) {
      setValue(name, transformedDefaultAttributes)
    }
  }, [currentValue, defaultAttributesSignature, setValue, name, transformedDefaultAttributes])

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Box>
          <FormLabel required={required} sx={{ color: grey[700], fontSize: '16px' }}>
            Attributes
          </FormLabel>
          <Autocomplete
            loading={isFetching || isLoading}
            multiple
            size="small"
            value={field.value || []}
            onChange={(_, newValue) => { field.onChange(newValue) }}
            options={displayedOptions}
            filterOptions={(options) => options}
            disableCloseOnSelect
            clearIcon={<CancelIcon sx={{ color: grey[950] }} />}
            getOptionLabel={(option) => option.title || option.value}
            isOptionEqualToValue={(option, value) => {
              // Handle different data structures
              const optionValue = option.value || option.attributeValue || option.values?.[0]
              const valueValue = value.value || value.attributeValue || value.values?.[0]
              const optionTitle = option.title || option.attributeKey || option.key
              const valueTitle = value.title || value.attributeKey || value.key

              return optionValue === valueValue && optionTitle === valueTitle
            }}
            onInputChange={(event, value, reason) => {
              if (reason === 'input') debouncedInputChange(value)
            }}
            popupIcon={<ExpandMoreIcon sx={{ color: grey[150] }} />}
            sx={{ mt: theme.spacing(2) }}
            PaperComponent={({ children }) => (
              <Paper elevation={4} >
                {children}
              </Paper>
            )}
            renderOption={(props, option, { selected }) => {
              const chipPalette = showAttributeTypeChip && option.attributeType
                ? ATTRIBUTE_TYPE_CHIP[option.attributeType]
                : undefined
              return (
                <li {...props} style={{ width: '98%' }}>
                  <Box
                    display="flex"
                    // Chip rows need full width + vertical centering to keep
                    // checkbox/label/chip on a single baseline. Non-chip consumers
                    // keep the pre-CE-39 layout (default cross-axis alignment;
                    // Checkbox vertical position is set by its sx mt offset).
                    {...(showAttributeTypeChip ? { alignItems: 'center', width: '100%' } : {})}
                  >
                    <Checkbox
                      icon={icon}
                      checkedIcon={<SelectedCheckBox width="18px" height="18px" />}
                      checked={selected}
                      sx={{ mt: theme.spacing(-3.5) }}
                      disableRipple
                    />
                    <Box display="flex" flexDirection="column">
                      <Typography fontSize="14px" color={grey[250]} fontWeight={500}>
                        {option.value || option.attributeValue || option.values?.[0]}
                      </Typography>
                      <Typography fontSize="14px" color={grey[850]}>
                        {option.title || option.attributeKey || option.key}
                      </Typography>
                    </Box>
                    {chipPalette && (
                      <Chip
                        size="small"
                        label={option.attributeType}
                        sx={{
                          ml: 'auto',
                          bgcolor: chipPalette.bg,
                          color: chipPalette.fg,
                          fontWeight: 500
                        }}
                      />
                    )}
                  </Box>
                </li>
              )
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                error={Boolean(errors[name])}
                helperText={errorMessage as string}
                placeholder={placeholder}
                sx={textFieldStyles}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: <> <SearchRoundedIcon style={{ color: grey[400] }} />  {params.InputProps.startAdornment}</>

                }}
              />
            )}
            renderTags={(value: any, getTagProps) =>
              value?.map((option: any, index: number) => {
                const { key, onDelete, ...tagProps } = getTagProps({ index })
                const optionValue = option.value || option.attributeValue || option.values?.[0]
                const optionTitle = option.title || option.attributeKey || option.key
                return (
                  <Chip
                    color='primary'
                    variant="filled"
                    label={optionValue || optionTitle}
                    key={key}
                    {...tagProps}
                    onDelete={disabled ? undefined : onDelete}
                    disabled={option.isInherited}
                    deleteIcon={disabled ? undefined : <CancelIcon sx={{ color: `${grey[400]} !important` }} />}
                  />
                )
              })
            }
            disabled={disabled}
            getOptionDisabled={(option) => getOptionDisabled?.(option) ??
              defaultAttributes?.some(
                (val: IAttribute) => {
                  const valValue = val.value
                  const optionValue = option.value || option.values?.[0]
                  return valValue === optionValue && val.isInherited
                }
              ) ?? false
            }
          />
        </Box>
      )}
    />
  )
}

export default AttributesMultiSelect
